import { Component, inject } from '@angular/core';
import { ClosetDoorsStandardService } from 'src/app/services/configurators-2d/applications/closet-doors-standard.service';
import { CurrentService } from 'src/app/services/current.service';
import { ModalService, MODAL } from 'src/app/services/modal.service';
import { Color } from 'src/app/models/configurators-2d/applications/closet-doors-standard/color';
import { Door } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door';
import { DoorSection } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door-section';
import { DoorType } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door-type';
import { DoorTypeSection } from 'src/app/models/configurators-2d/applications/closet-doors-standard/door-type-section';
import { Layer } from 'src/app/models/configurators-2d/applications/closet-doors-standard/layer';
import { FinishesSubStep } from '../../models/finishes-sub-step.enum';
import { FinishesSubStepService } from '../../services/finishes-sub-step.service';

@Component({
  selector: 'ng-casto-closet-doors-standard-finishes-doors',
  templateUrl: './doors.component.html',
  styleUrls: ['./doors.component.scss'],
})
export class ClosetDoorsStandardFinishesDoorsComponent {
  readonly SubStep = FinishesSubStep;

  private readonly modalService = inject(ModalService);
  private readonly closetDoorsService = inject(ClosetDoorsStandardService);
  private readonly current = inject(CurrentService);
  private readonly subStepService = inject(FinishesSubStepService);

  readonly baseImgURL = this.closetDoorsService.apiUrl;

  get subStep(): FinishesSubStep {
    return this.subStepService.subStep();
  }
  get doorTypes(): DoorType[] {
    return this.closetDoorsService.structures;
  }
  get selectedDoorId(): number | undefined {
    return this.current.closetDoors.selectedDoorId;
  }
  get doors(): Door[] {
    return this.current.closetDoors.doors;
  }

  toggleCollapse(): void {
    this.subStepService.toggle(FinishesSubStep.Doors);
  }

  /** Affiche la modale d'informations sur les structures et replie toutes les cartes. */
  showInfoModal(): void {
    this.subStepService.close();
    this.modalService.display(MODAL.CLOSET_DOORS_STRUCTURE_INFO, {});
  }

  /**
   * Sélectionne une structure pour la porte courante.
   * Si incompatible avec les autres structures, applique à toutes les portes.
   * Les couleurs incompatibles avec la nouvelle structure sont réinitialisées.
   */
  selectDoorType(structure: DoorType): void {
    if (this.selectedDoorId === undefined) {
      return;
    }

    const selectedDoor = this.doors.find((d: Door) => d.id === this.selectedDoorId);

    if (!selectedDoor) {
      return;
    }

    this.applyDoorTypeToDoor(selectedDoor, structure);
    selectedDoor.layer = this.closetDoorsService.layers?.find(
      (l: Layer) => l.frameFinishId === this.current.closetDoors.frameFinish?.id && l.doorTypeId === structure.id
    );

    this.current.closetDoors.selectedSectionIndex = undefined;
    this.closetDoorsService.updateLayers();
    this.scrollDown();
  }

  /** Indique si la structure est sélectionnée pour la porte courante. */
  isDoorTypeSelected(id: number): boolean {
    if (this.selectedDoorId === undefined) return false;
    const door = this.doors.find((d: Door) => d.id === this.selectedDoorId);
    return door?.structure?.id === id;
  }

  /**
   * Applique une structure à un vantail : reconstruit ses sections en conservant
   * les couleurs compatibles depuis les sections précédentes.
   */
  private applyDoorTypeToDoor(door: Door, structure: DoorType): void {
    door.sections = this.rebuildSections(structure, door.sections ?? []);
    door.structure = structure;
  }

  /**
   * Reconstruit les sections d'un vantail à partir d'une nouvelle structure,
   * en conservant les couleurs compatibles de l'ancienne configuration.
   */
  private rebuildSections(structure: DoorType, prevSections: DoorSection[]): DoorSection[] {
    return (structure.sections ?? []).map((s: DoorTypeSection) => {
      const prev = prevSections.find((ps: DoorSection) => ps.index === s.sectionIndex);

      const ids: number[] = s.availableColorIds ?? [];

      const colorIsCompatible = !s.isFixed && prev?.color &&
        (ids.length === 0 || ids.includes(prev.color.id));

      const fixedColor = s.isFixed && s.fixedColorId
        ? (this.closetDoorsService.decorations?.find((d: Color) => d.id === s.fixedColorId) ?? null)
        : null;

      return {
        index: s.sectionIndex,
        heightPercentage: s.heightPercentage,
        isFixed: s.isFixed,
        fixedColorId: s.fixedColorId ?? null,
        color: s.isFixed ? fixedColor : (colorIsCompatible ? (prev?.color ?? null) : null),
        availableColorIds: ids,
        linkedSectionIndexes: s.linkedSectionIndexes ?? [],
      };
    });
  }

  private scrollDown(): void {
    if (document.documentElement.clientWidth < 767) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }
}
